import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdVisibility, MdCloudDownload, MdPublic } from 'react-icons/md';
import { Modal } from '../../../components/shared/Modal';
import { useToast } from '../../../components/shared/Toast';
import { Pagination } from '../../../components/shared/Pagination';
import { SearchableSelect } from '../../../components/shared/SearchableSelect';
import { authenticatedFetch } from '../../../lib/api';
import { PortForm } from './PortForm';
import { PortDetailPanel } from './PortDetailPanel';
import { UnlocodeBrowser } from './UnlocodeBrowser';
import { TableBodySkeleton, ClearFiltersButton, erpPageBtnPrimaryClass, erpPageBtnSecondaryClass } from '@euroasiann/shared/ui';
import { useDebouncedSearch } from '@euroasiann/shared';
import {
    pmTableWrapClass,
    pmThClass,
    pmTdClass,
    pmTrClass,
    pmIconBtnClass,
    pmDangerIconBtnClass,
    pmStatusClass,
    pmSelectBtnClass,
} from '../port-management.ui';

interface Port {
    _id: string;
    name: string;
    code: string;
    country: string;
    countryName?: string;
    type: string;
    status: string;
    city?: string;
    state?: string;
    stateName?: string;
    latitude?: number;
    longitude?: number;
    terminalCount?: number;
    berthCount?: number;
    chargeCount?: number;
}

export function PortList() {
    const [page, setPage] = useState(1);
    const {
        inputValue: search,
        submittedValue: submittedSearch,
        setInputValue: setSearch,
        clear: clearSearch,
        onKeyDown: onSearchKeyDown,
    } = useDebouncedSearch('', {
        onCommit: () => setPage(1),
    });
    const [country, setCountry] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [status, setStatus] = useState('');
    const [limit, setLimit] = useState(10);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isUnlocodeOpen, setIsUnlocodeOpen] = useState(false);
    const [selectedPort, setSelectedPort] = useState<Port | null>(null);
    const [detailPortId, setDetailPortId] = useState<string | null>(null);
    const { success, error } = useToast();
    const queryClient = useQueryClient();

    const { data: unlocodeStats } = useQuery({
        queryKey: ['unlocode-stats'],
        queryFn: async () => {
            const res = await authenticatedFetch('/api/v1/ports/reference/unlocode/stats');
            const json = await res.json();
            return json.data;
        },
    });

    const { data: filterOptions } = useQuery({
        queryKey: ['port-filters', country, state],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (country) params.set('country', country);
            if (state) params.set('state', state);
            const qs = params.toString();
            const res = await authenticatedFetch(`/api/v1/ports/filters${qs ? `?${qs}` : ''}`);
            const json = await res.json();
            return json.data;
        },
    });

    const importAllMutation = useMutation({
        mutationFn: async () => {
            const res = await authenticatedFetch('/api/v1/ports/sync/unlocode', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ importAll: true, includeChargeTemplates: false }),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Worldwide import failed');
            return json.data;
        },
        onSuccess: (result) => {
            success(result.message || `Imported ${result.total} ports (${result.created} new, ${result.updated} updated)`);
            queryClient.invalidateQueries({ queryKey: ['ports'] });
            queryClient.invalidateQueries({ queryKey: ['unlocode-stats'] });
            queryClient.invalidateQueries({ queryKey: ['port-filters'] });
        },
        onError: (err: Error) => error(err.message),
    });

    const handleImportAllWorldwide = () => {
        const total = unlocodeStats?.totalSeaports?.toLocaleString() ?? 'all';
        const confirmed = window.confirm(
            `Import / refresh ALL ${total} worldwide seaports from UN/LOCODE?\n\n` +
            'Updates: name, UN/LOCODE, country, state, city, coordinates.\n' +
            'May take 2–5 minutes. Re-run anytime to fix missing ports (e.g. Visakhapatnam INVTZ).\n\n' +
            'Continue?'
        );
        if (confirmed) importAllMutation.mutate();
    };

    const { data, isLoading } = useQuery({
        queryKey: ['ports', submittedSearch, country, city, state, status, page, limit],
        queryFn: async ({ signal }) => {
            const params = new URLSearchParams();
            if (submittedSearch) params.append('search', submittedSearch);
            if (country) params.append('country', country);
            if (city) params.append('city', city);
            if (state) params.append('state', state);
            if (status) params.append('status', status);
            params.append('page', page.toString());
            params.append('limit', limit.toString());
            params.append('directory', 'true');

            const res = await authenticatedFetch(`/api/v1/ports?${params.toString()}`, { signal });
            return res.json();
        },
    });

    const ports = data?.data || [];
    const meta = data?.meta || { total: 0, page: 1, limit: 10 };
    const totalPages = Math.ceil(meta.total / meta.limit);

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const res = await authenticatedFetch(`/api/v1/ports/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error('Failed to delete port');
        },
        onSuccess: () => {
            success('Port deleted successfully');
            queryClient.invalidateQueries({ queryKey: ['ports'] });
        },
        onError: (err: Error) => error(err.message),
    });

    const clearFilters = () => {
        clearSearch();
        setCountry('');
        setCity('');
        setState('');
        setStatus('');
        setPage(1);
    };

    const hasFilters = search || country || city || state || status;

    const countryOptions = useMemo(
        () => (filterOptions?.countries || []).map((c: { code: string; name: string }) => ({
            value: c.code,
            label: c.name,
            sublabel: c.code,
        })),
        [filterOptions?.countries],
    );

    const stateOptions = useMemo(
        () =>
            (filterOptions?.states || []).map((s: { code: string; name: string }) => ({
                value: s.code,
                label: s.name || s.code,
                sublabel: s.name && s.code !== s.name ? s.code : undefined,
            })),
        [filterOptions?.states],
    );

    const cityOptions = useMemo(
        () => (filterOptions?.cities || []).map((c: { code: string; name: string }) => ({
            value: c.code,
            label: c.name,
        })),
        [filterOptions?.cities],
    );

    const statusOptions = useMemo(
        () => [
            { value: 'Active', label: 'Active' },
            { value: 'Inactive', label: 'Inactive' },
        ],
        [],
    );

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-[hsl(var(--foreground))]">UN/LOCODE registry</p>
                    <p className="mt-0.5 text-sm text-[hsl(var(--muted-foreground))]">
                        {unlocodeStats
                            ? `${unlocodeStats.totalSeaports?.toLocaleString()} seaports in dataset · ${unlocodeStats.inRegistry?.toLocaleString()} in your registry`
                            : 'Loading global port count...'}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={handleImportAllWorldwide}
                    disabled={importAllMutation.isPending}
                    className={erpPageBtnPrimaryClass}
                >
                    <MdPublic className={`h-5 w-5 ${importAllMutation.isPending ? 'animate-spin' : ''}`} />
                    {importAllMutation.isPending ? 'Importing...' : 'Fetch worldwide ports'}
                </button>
            </div>

            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-[hsl(var(--foreground))]">Filters</p>
                    {meta.total > 0 && (
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">
                            {meta.total.toLocaleString()} matching ports
                        </span>
                    )}
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
                    <div className="relative lg:col-span-2">
                        <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                        <input
                            type="search"
                            placeholder="Search name, code, or city"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={onSearchKeyDown}
                            className="h-10 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] py-0 pl-9 pr-3 text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:border-[#0088cc] focus:outline-none focus:ring-1 focus:ring-[#0088cc]"
                        />
                    </div>
                    <SearchableSelect
                        value={country}
                        onChange={(v) => { setCountry(v); setCity(''); setState(''); setPage(1); }}
                        options={countryOptions}
                        allLabel="All countries"
                        searchPlaceholder="Search country..."
                        buttonClassName={pmSelectBtnClass}
                    />
                    <SearchableSelect
                        value={state}
                        onChange={(v) => { setState(v); setCity(''); setPage(1); }}
                        options={stateOptions}
                        allLabel="All states / provinces"
                        searchPlaceholder={country ? 'Search state...' : 'Select country first'}
                        disabled={!country}
                        buttonClassName={pmSelectBtnClass}
                    />
                    <SearchableSelect
                        value={city}
                        onChange={(v) => { setCity(v); setPage(1); }}
                        options={cityOptions}
                        allLabel="All cities"
                        searchPlaceholder={state ? 'Search city...' : country ? 'Select state first' : 'Select country first'}
                        disabled={!country || !state}
                        buttonClassName={pmSelectBtnClass}
                    />
                    <SearchableSelect
                        value={status}
                        onChange={(v) => { setStatus(v); setPage(1); }}
                        options={statusOptions}
                        allLabel="All statuses"
                        searchPlaceholder="Search status..."
                        buttonClassName={pmSelectBtnClass}
                    />
                </div>
                {hasFilters && (
                    <div className="mt-3">
                        <ClearFiltersButton visible onClick={clearFilters} variant="toolbar" />
                    </div>
                )}
            </div>

            <div className="flex flex-wrap justify-end gap-2">
                <button type="button" onClick={() => setIsUnlocodeOpen(true)} className={erpPageBtnSecondaryClass}>
                    <MdCloudDownload className="h-4 w-4" />
                    Import selected
                </button>
                <button
                    type="button"
                    onClick={() => { setSelectedPort(null); setIsFormOpen(true); }}
                    className={erpPageBtnPrimaryClass}
                >
                    <MdAdd className="h-4 w-4" />
                    Add port
                </button>
            </div>

            <div className={pmTableWrapClass}>
                <table className="w-full min-w-[900px] border-collapse">
                    <thead>
                        <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
                            {['Port / UN/LOCODE', 'Location', 'Coordinates', 'Type', 'Terminals · Berths', 'Tariff rules', 'Status', 'Actions'].map((h) => (
                                <th key={h} className={pmThClass}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <TableBodySkeleton rows={6} cols={8} />
                        ) : ports.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="px-4 py-16 text-center">
                                    <p className="text-sm font-medium text-[hsl(var(--foreground))]">No ports found</p>
                                    <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
                                        Fetch worldwide ports, then search by name or filter by country.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleImportAllWorldwide}
                                        className="mt-3 text-sm font-semibold text-[#0088cc] hover:underline"
                                    >
                                        Fetch all ports
                                    </button>
                                </td>
                            </tr>
                        ) : ports.map((port: Port) => (
                            <tr key={port._id} className={pmTrClass}>
                                <td className={pmTdClass}>
                                    <div className="font-medium">{port.name}</div>
                                    <div className="mt-0.5 font-mono text-xs text-[#0088cc]">{port.code}</div>
                                </td>
                                <td className={pmTdClass}>
                                    <div>{[port.city, port.stateName || port.state].filter(Boolean).join(', ') || '—'}</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                                        {port.countryName || port.country}
                                    </div>
                                </td>
                                <td className={`${pmTdClass} font-mono text-xs text-[hsl(var(--muted-foreground))]`}>
                                    {port.latitude != null && port.longitude != null
                                        ? `${port.latitude.toFixed(4)}, ${port.longitude.toFixed(4)}`
                                        : '—'}
                                </td>
                                <td className={pmTdClass}>
                                    <span className="rounded-md bg-[hsl(var(--muted))] px-2 py-0.5 text-xs font-medium">
                                        {port.type || 'Seaport'}
                                    </span>
                                </td>
                                <td className={`${pmTdClass} text-[hsl(var(--muted-foreground))]`}>
                                    {(port.terminalCount ?? 0)} terminal{(port.terminalCount ?? 0) === 1 ? '' : 's'} ·{' '}
                                    {(port.berthCount ?? 0)} berth{(port.berthCount ?? 0) === 1 ? '' : 's'}
                                </td>
                                <td className={pmTdClass}>
                                    <span className={(port.chargeCount ?? 0) > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-[hsl(var(--muted-foreground))]'}>
                                        {(port.chargeCount ?? 0) > 0
                                            ? `${port.chargeCount} rule${port.chargeCount === 1 ? '' : 's'}`
                                            : 'No rules'}
                                    </span>
                                </td>
                                <td className={pmTdClass}>
                                    <span className={pmStatusClass(port.status === 'Active')}>{port.status}</span>
                                </td>
                                <td className={pmTdClass}>
                                    <div className="flex items-center gap-0.5">
                                        <button type="button" onClick={() => setDetailPortId(port._id)} className={pmIconBtnClass} title="View details">
                                            <MdVisibility className="h-4 w-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setSelectedPort(port); setIsFormOpen(true); }}
                                            className={pmIconBtnClass}
                                            title="Edit"
                                        >
                                            <MdEdit className="h-4 w-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { if (window.confirm('Delete this port?')) deleteMutation.mutate(port._id); }}
                                            className={pmDangerIconBtnClass}
                                            title="Delete"
                                        >
                                            <MdDelete className="h-4 w-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {meta.total > 0 && (
                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    itemsPerPage={limit}
                    totalItems={meta.total}
                    onItemsPerPageChange={(newLimit) => { setLimit(newLimit); setPage(1); }}
                />
            )}

            <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedPort ? 'Edit port' : 'Add port'}>
                <PortForm
                    initialData={selectedPort}
                    onSuccess={() => { setIsFormOpen(false); queryClient.invalidateQueries({ queryKey: ['ports'] }); }}
                    onCancel={() => setIsFormOpen(false)}
                />
            </Modal>

            {isUnlocodeOpen && <UnlocodeBrowser onClose={() => setIsUnlocodeOpen(false)} />}
            {detailPortId && <PortDetailPanel portId={detailPortId} onClose={() => setDetailPortId(null)} />}
        </div>
    );
}
